
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface FeatureFlag {
  id: string;
  flag_name: string;
  display_name: string;
  description?: string;
  is_enabled: boolean;
  target_plans: string[];
  conditions: any;
  created_at: string;
  updated_at?: string;
}

export const useFeatureFlags = () => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFeatureFlags = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .order('display_name', { ascending: true });

      if (error) throw error;
      setFeatureFlags(data || []);
    } catch (err) {
      console.error('Error fetching feature flags:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch feature flags');
    } finally {
      setLoading(false);
    }
  };

  const updateFeatureFlag = async (flagName: string, isEnabled: boolean) => {
    try {
      const { error } = await supabase
        .from('feature_flags')
        .update({ 
          is_enabled: isEnabled,
          updated_at: new Date().toISOString()
        })
        .eq('flag_name', flagName);

      if (error) throw error;
      
      // Update local state
      setFeatureFlags(prev => 
        prev.map(flag => 
          flag.flag_name === flagName ? { ...flag, is_enabled: isEnabled } : flag
        )
      );
    } catch (err) {
      console.error('Error updating feature flag:', err);
      throw err;
    }
  };

  const isFeatureEnabled = (flagName: string, userPlan: string = 'free'): boolean => {
    const flag = featureFlags.find(f => f.flag_name === flagName);
    if (!flag) return false;
    
    return flag.is_enabled && flag.target_plans.includes(userPlan);
  };

  useEffect(() => {
    fetchFeatureFlags();

    // Set up real-time subscription
    const channel = supabase
      .channel(`feature-flags-changes-${Math.random()}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'feature_flags'
      }, () => {
        fetchFeatureFlags();
      })
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch (error) {
        console.error('Error cleaning up feature flags subscription:', error);
      }
    };
  }, []);

  return {
    featureFlags,
    loading,
    error,
    updateFeatureFlag,
    isFeatureEnabled,
    refetch: fetchFeatureFlags
  };
};
